import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Constants ───────────────────────────────────────────────────────────────
const DOC_STATUS = {
  pending:  { label: 'Under Review', bg: '#dbeafe', color: '#1e40af', icon: '🔍' },
  approved: { label: 'Approved',     bg: '#d1fae5', color: '#065f46', icon: '✅' },
  rejected: { label: 'Action Needed', bg: '#fee2e2', color: '#991b1b', icon: '⚠️' },
};

const RAG = {
  green: { dot: '#10b981', ring: '#d1fae5', text: '#065f46' },
  amber: { dot: '#f59e0b', ring: '#fef3c7', text: '#92400e' },
  red:   { dot: '#ef4444', ring: '#fee2e2', text: '#991b1b' },
};

// ─── Stage progress tracker ───────────────────────────────────────────────────
function StageTracker({ stages, currentStageId, currentPosition }) {
  if (!stages?.length) return null;
  const visible = stages.filter(s => !s.is_terminal || s.id === currentStageId);
  const currentIdx = visible.findIndex(s => s.id === currentStageId);
  const pct = visible.length > 1 ? Math.round((currentIdx / (visible.length - 1)) * 100) : currentIdx >= 0 ? 100 : 0;

  return (
    <div>
      <div style={{ position: 'relative', paddingTop: 4 }}>
        {/* Track line */}
        <div style={{ position: 'absolute', top: 18, left: 18, right: 18, height: 3, background: 'rgba(255,255,255,0.2)', borderRadius: 99 }} />
        {/* Progress fill */}
        <div style={{ position: 'absolute', top: 18, left: 18, height: 3, background: '#fff', borderRadius: 99, width: `calc(${pct}% - 18px)`, transition: 'width 0.6s ease' }} />
        {/* Stage dots */}
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1, overflowX: 'auto', gap: 4 }}>
          {visible.map((stage, i) => {
            const isCurrent = stage.id === currentStageId;
            const isDone = currentPosition != null && stage.position < currentPosition;
            const rag = RAG[stage.rag_colour] || RAG.green;
            return (
              <div key={stage.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 52, maxWidth: 80 }}>
                <div style={{
                  width: isCurrent ? 36 : 30, height: isCurrent ? 36 : 30, borderRadius: '50%', flexShrink: 0,
                  background: isDone ? '#fff' : isCurrent ? '#fff' : 'rgba(255,255,255,0.25)',
                  border: `3px solid ${isDone ? '#fff' : isCurrent ? '#fff' : 'rgba(255,255,255,0.4)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: isCurrent ? '0 0 0 5px rgba(255,255,255,0.2)' : 'none',
                  transition: 'all 0.3s',
                }}>
                  {isDone ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7L5.5 10L11.5 4" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : isCurrent ? (
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} />
                  ) : (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.5)' }} />
                  )}
                </div>
                <div style={{ marginTop: 8, fontSize: 10, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? '#fff' : 'rgba(255,255,255,0.65)', textAlign: 'center', lineHeight: 1.3, maxWidth: 72 }}>
                  {stage.name}
                </div>
                {isCurrent && <div style={{ marginTop: 3, fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Now</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main portal ──────────────────────────────────────────────────────────────
export default function ClientPortal() {
  const navigate = useNavigate();
  const [deal, setDeal]             = useState(null);
  const [brokerInfo, setBrokerInfo] = useState(null);
  const [brokerDocs, setBrokerDocs] = useState([]);
  const [messages, setMessages]     = useState([]);
  const [newMsg, setNewMsg]         = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(true);
  const [allStages, setAllStages]   = useState([]);
  const [uploading, setUploading]   = useState({});
  const [uploadErr, setUploadErr]   = useState({});
  const [section, setSection]       = useState('documents');
  const fileRefs = useRef({});
  const msgEndRef = useRef();

  const token = localStorage.getItem('clientToken');
  const authH = () => ({ Authorization: `Bearer ${token}` });

  const loadAll = async () => {
    try {
      const [dealRes, brokerRes, brokerDocsRes, msgsRes] = await Promise.all([
        fetch('/api/client-portal/deal',             { headers: authH() }),
        fetch('/api/client-portal/broker-info',      { headers: authH() }),
        fetch('/api/client-portal/broker-documents', { headers: authH() }),
        fetch('/api/client-portal/messages',         { headers: authH() }),
      ]);
      if (dealRes.status === 401) { localStorage.removeItem('clientToken'); navigate('/client/login'); return; }
      const dealData = await dealRes.json();
      if (dealData.error) { setError(dealData.error); return; }
      setDeal({
        ...dealData.deal,
        docTypes:  dealData.requiredDocTypes  || [],
        documents: (dealData.uploadedDocuments || []).map(d => ({
          ...d,
          status: d.broker_status || 'pending',
        })),
      });
      setAllStages(dealData.allStages || []);
      if (brokerRes.ok)     setBrokerInfo(await brokerRes.json());
      if (brokerDocsRes.ok) setBrokerDocs((await brokerDocsRes.json()).documents || []);
      if (msgsRes.ok)       setMessages((await msgsRes.json()).messages || []);
    } catch { setError('Failed to load your portal. Please try again.'); }
    finally   { setLoading(false); }
  };

  useEffect(() => { if (!token) { navigate('/client/login'); return; } loadAll(); }, []); // eslint-disable-line
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleUpload = async (docTypeId, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [docTypeId]: true }));
    setUploadErr(u => ({ ...u, [docTypeId]: null }));
    const form = new FormData();
    form.append('file', file);
    form.append('document_type_id', docTypeId);
    try {
      const res  = await fetch('/api/client-portal/documents', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
      const data = await res.json();
      if (!res.ok) setUploadErr(u => ({ ...u, [docTypeId]: data.error || 'Upload failed.' }));
      else loadAll();
    } catch { setUploadErr(u => ({ ...u, [docTypeId]: 'Upload failed. Please try again.' })); }
    finally   { setUploading(u => ({ ...u, [docTypeId]: false })); }
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    setSendingMsg(true);
    try {
      await fetch('/api/client-portal/messages', { method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ message: newMsg.trim() }) });
      setNewMsg(''); loadAll();
    } catch {} finally { setSendingMsg(false); }
  };

  const downloadBrokerDoc = async (docId, filename) => {
    const res = await fetch(`/api/client-portal/documents/${docId}/download`, { headers: authH() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = filename || 'document'; a.click(); URL.revokeObjectURL(url);
  };

  const markMessagesRead = () => {
    fetch('/api/client-portal/messages/read', { method: 'POST', headers: authH() }).catch(() => {});
    setMessages(m => m.map(x => x.sender_role === 'broker' ? { ...x, read_at: new Date().toISOString() } : x));
  };

  const logout = () => { localStorage.removeItem('clientToken'); localStorage.removeItem('clientDealId'); navigate('/client/login'); };

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0e1b2c 0%, #1a3a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🏡</div>
        <div style={{ fontSize: 16, fontWeight: 600, opacity: 0.8 }}>Loading your portal…</div>
      </div>
    </div>
  );
  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0e1b2c', marginBottom: 8 }}>Something went wrong</div>
        <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>{error}</div>
        <button onClick={() => { setLoading(true); setError(''); loadAll(); }} style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
      </div>
    </div>
  );

  // ── Derived state ────────────────────────────────────────────────────────
  const uploadedCount  = deal.docTypes.filter(dt => deal.documents.some(d => d.document_type_id === dt.id && d.status !== 'rejected')).length;
  const allDone        = deal.docTypes.length > 0 && uploadedCount === deal.docTypes.length;
  const rejectedDocs   = deal.docTypes.filter(dt => { const last = deal.documents.filter(d => d.document_type_id === dt.id).at(-1); return last?.status === 'rejected'; });
  const unreadMsgs     = messages.filter(m => m.sender_role === 'broker' && !m.read_at).length;
  const hasAlerts      = rejectedDocs.length > 0 || unreadMsgs > 0 || brokerDocs.length > 0;

  const navItems = [
    { id: 'documents',   label: 'Documents',    icon: '📄', badge: rejectedDocs.length },
    { id: 'messages',    label: 'Messages',     icon: '💬', badge: unreadMsgs, onEnter: markMessagesRead },
    { id: 'broker-docs', label: 'From Broker',  icon: '📁', badge: brokerDocs.length, hidden: brokerDocs.length === 0 },
  ].filter(n => !n.hidden);

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* ─── TOP BAR ─── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#0e1b2c', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>🏡</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>BrokerConnect</span>
            <span style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)', display: 'inline-block' }} />
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{deal.company_name}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{deal.client_name}</span>
            <button onClick={logout} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', padding: '5px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Log out
            </button>
          </div>
        </div>
      </div>

      {/* ─── HERO BANNER ─── */}
      <div style={{ background: 'linear-gradient(135deg, #0e1b2c 0%, #1a3a5c 55%, #0f766e 100%)', padding: '32px 16px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <div style={{ position: 'absolute', bottom: -30, right: '20%', width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Your Application</p>
          <h1 style={{ margin: '0 0 4px', fontSize: 26, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Hello, {deal.client_name.split(' ')[0]} 👋</h1>
          <p style={{ margin: '0 0 28px', fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
            {deal.product_type ? `${deal.product_type} · ` : ''}{deal.company_name}
          </p>

          {/* Current stage badge */}
          {deal.stage_name && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 12, padding: '10px 16px', marginBottom: 28 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{deal.stage_name}</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>— current stage</span>
            </div>
          )}

          {/* Stage tracker */}
          {allStages.length > 0 && (
            <StageTracker stages={allStages} currentStageId={deal.deal_stage_id} currentPosition={deal.stage_position} />
          )}

          {/* Doc progress bar */}
          {deal.docTypes.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Documents</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: allDone ? '#34d399' : '#fff' }}>
                  {allDone ? '✅ All uploaded' : `${uploadedCount} of ${deal.docTypes.length} uploaded`}
                </span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 99 }}>
                <div style={{ height: '100%', background: allDone ? '#34d399' : '#10b981', borderRadius: 99, width: `${deal.docTypes.length ? (uploadedCount / deal.docTypes.length) * 100 : 0}%`, transition: 'width 0.5s ease' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── MAIN CONTENT ─── */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px 60px' }}>

        {/* ─── ALERT STRIP ─── */}
        {hasAlerts && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20, marginBottom: 4 }}>
            {rejectedDocs.length > 0 && (
              <div onClick={() => setSection('documents')} style={{ background: '#fff', border: '1px solid #fca5a5', borderLeft: '4px solid #ef4444', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#991b1b' }}>Action required — {rejectedDocs.length} document{rejectedDocs.length !== 1 ? 's' : ''} need re-uploading</div>
                  <div style={{ fontSize: 12, color: '#ef4444', marginTop: 2 }}>{rejectedDocs.map(d => d.name).join(', ')}</div>
                </div>
                <span style={{ color: '#ef4444', fontSize: 18 }}>›</span>
              </div>
            )}
            {unreadMsgs > 0 && (
              <div onClick={() => { setSection('messages'); markMessagesRead(); }} style={{ background: '#fff', border: '1px solid #93c5fd', borderLeft: '4px solid #3b82f6', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize: 20 }}>💬</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af' }}>{unreadMsgs} new message{unreadMsgs !== 1 ? 's' : ''} from your broker</div>
                  <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>Tap to read and reply</div>
                </div>
                <span style={{ color: '#3b82f6', fontSize: 18 }}>›</span>
              </div>
            )}
            {brokerDocs.length > 0 && section !== 'broker-docs' && (
              <div onClick={() => setSection('broker-docs')} style={{ background: '#fff', border: '1px solid #6ee7b7', borderLeft: '4px solid #10b981', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <span style={{ fontSize: 20 }}>📁</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{brokerDocs.length} document{brokerDocs.length !== 1 ? 's' : ''} from your broker</div>
                  <div style={{ fontSize: 12, color: '#10b981', marginTop: 2 }}>Available to download</div>
                </div>
                <span style={{ color: '#10b981', fontSize: 18 }}>›</span>
              </div>
            )}
          </div>
        )}

        {/* ─── NAV TABS ─── */}
        <div style={{ display: 'flex', gap: 6, margin: '20px 0 20px', background: '#fff', borderRadius: 14, padding: 5, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
          {navItems.map(nav => (
            <button key={nav.id} onClick={() => { setSection(nav.id); nav.onEnter?.(); }} style={{
              flex: 1, padding: '11px 8px', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s',
              background: section === nav.id ? '#0e1b2c' : 'transparent',
              color: section === nav.id ? '#fff' : '#64748b',
            }}>
              <span>{nav.icon}</span>
              <span>{nav.label}</span>
              {nav.badge > 0 && (
                <span style={{ background: section === nav.id ? 'rgba(255,255,255,0.25)' : '#ef4444', color: '#fff', borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700, lineHeight: '18px' }}>{nav.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── DOCUMENTS SECTION ─── */}
        {section === 'documents' && (
          <div>
            {deal.docTypes.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>No documents requested yet</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Your broker will let you know when documents are needed.</div>
              </div>
            ) : (
              deal.docTypes.map(dt => {
                const existing  = deal.documents.filter(d => d.document_type_id === dt.id);
                const latest    = existing.at(-1);
                const status    = latest?.status || 'not_uploaded';
                const isApproved = status === 'approved';
                const isRejected = status === 'rejected';
                const ds         = DOC_STATUS[status];

                return (
                  <div key={dt.id} style={{
                    background: '#fff', borderRadius: 16, marginBottom: 14,
                    border: `1px solid ${isRejected ? '#fca5a5' : isApproved ? '#6ee7b7' : '#e2e8f0'}`,
                    borderLeft: `4px solid ${isRejected ? '#ef4444' : isApproved ? '#10b981' : '#94a3b8'}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden',
                  }}>
                    <div style={{ padding: '18px 20px' }}>
                      {/* Doc type header */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#0e1b2c' }}>{dt.name}</span>
                            {latest && ds && (
                              <span style={{ fontSize: 11, fontWeight: 700, background: ds.bg, color: ds.color, borderRadius: 99, padding: '2px 10px' }}>
                                {ds.icon} {ds.label}
                              </span>
                            )}
                            {!latest && (
                              <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 99, padding: '2px 10px' }}>📤 Not uploaded</span>
                            )}
                          </div>
                          {dt.description && <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{dt.description}</p>}
                        </div>
                      </div>

                      {/* Rejection note */}
                      {isRejected && latest?.broker_note && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 12 }}>
                          <strong>📋 Broker note:</strong> {latest.broker_note}
                        </div>
                      )}

                      {/* Previously uploaded filename */}
                      {latest && !isRejected && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 12, color: '#94a3b8', background: '#f8fafc', borderRadius: 8, padding: '6px 10px', width: 'fit-content' }}>
                          <span>📎</span>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{latest.original_filename}</span>
                          <span>·</span>
                          <span>{new Date(latest.created_at).toLocaleDateString('en-GB')}</span>
                        </div>
                      )}

                      {/* Upload button */}
                      <input type="file" accept=".pdf,image/*" ref={el => { fileRefs.current[dt.id] = el; }} style={{ display: 'none' }}
                        onChange={e => handleUpload(dt.id, e.target.files[0])} />
                      {!isApproved && (
                        <button onClick={() => fileRefs.current[dt.id]?.click()} disabled={uploading[dt.id]}
                          style={{
                            background: isRejected ? '#ef4444' : '#0e1b2c', color: '#fff',
                            border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 700, cursor: uploading[dt.id] ? 'not-allowed' : 'pointer',
                            opacity: uploading[dt.id] ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 8,
                          }}>
                          {uploading[dt.id] ? (
                            <><span>⏳</span> Uploading…</>
                          ) : isRejected ? (
                            <><span>🔄</span> Re-upload document</>
                          ) : (
                            <><span>📤</span> Upload document</>
                          )}
                        </button>
                      )}
                      {isApproved && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#065f46' }}>
                          <span>✅</span> Approved — no further action needed
                        </div>
                      )}

                      {uploadErr[dt.id] && (
                        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>{uploadErr[dt.id]}</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ─── MESSAGES SECTION ─── */}
        {section === 'messages' && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            {/* Messages header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, #0e1b2c, #1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🏛️</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0e1b2c' }}>{brokerInfo?.broker_name || brokerInfo?.company_name || 'Your Broker'}</div>
                {brokerInfo?.company_name && brokerInfo?.broker_name && <div style={{ fontSize: 12, color: '#94a3b8' }}>{brokerInfo.company_name}</div>}
              </div>
            </div>

            {/* Message thread */}
            <div style={{ padding: '16px 20px', minHeight: 240, maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, background: '#fafafa' }}>
              {messages.length === 0 && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                  <div><div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>No messages yet.<br />Send a message to your broker below.</div>
                </div>
              )}
              {messages.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_role === 'client' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '76%', borderRadius: m.sender_role === 'client' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    padding: '10px 14px', fontSize: 14,
                    background: m.sender_role === 'client' ? '#0e1b2c' : '#fff',
                    color: m.sender_role === 'client' ? '#fff' : '#0e1b2c',
                    border: m.sender_role === 'client' ? 'none' : '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 5, opacity: 0.6 }}>
                      {m.sender_role === 'client' ? 'You' : m.sender_name || 'Broker'}
                    </div>
                    <div style={{ lineHeight: 1.5 }}>{m.message}</div>
                    <div style={{ fontSize: 11, opacity: 0.5, marginTop: 6, textAlign: 'right' }}>
                      {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>

            {/* Message input */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
              <input
                value={newMsg} onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMsg()}
                placeholder="Message your broker…"
                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', background: '#f8fafc', color: '#0e1b2c' }}
              />
              <button onClick={sendMsg} disabled={sendingMsg || !newMsg.trim()}
                style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: !newMsg.trim() ? 'not-allowed' : 'pointer', opacity: !newMsg.trim() ? 0.4 : 1, flexShrink: 0 }}>
                Send
              </button>
            </div>
          </div>
        )}

        {/* ─── BROKER DOCS SECTION ─── */}
        {section === 'broker-docs' && (
          <div>
            {brokerDocs.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📁</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>No documents from your broker yet</div>
              </div>
            ) : (
              brokerDocs.map(doc => (
                <div key={doc.id} style={{ background: '#fff', borderRadius: 14, marginBottom: 12, border: '1px solid #e2e8f0', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: '#f0fdf4', border: '1px solid #6ee7b7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📄</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_filename}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                      Shared {new Date(doc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {doc.uploaded_by_name && ` · ${doc.uploaded_by_name}`}
                    </div>
                  </div>
                  <button onClick={() => downloadBrokerDoc(doc.id, doc.original_filename)}
                    style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>⬇️</span> Download
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* ─── BROKER CONTACT CARD ─── */}
        {brokerInfo && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px', marginTop: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #0e1b2c, #1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏛️</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0e1b2c' }}>{brokerInfo.company_name}</div>
                {brokerInfo.broker_name && <div style={{ fontSize: 13, color: '#64748b' }}>Your broker: {brokerInfo.broker_name}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {brokerInfo.broker_phone && (
                <a href={`tel:${brokerInfo.broker_phone}`} style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 700, color: '#065f46', textDecoration: 'none' }}>
                  📞 {brokerInfo.broker_phone}
                </a>
              )}
              {brokerInfo.broker_email && (
                <a href={`mailto:${brokerInfo.broker_email}`} style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 700, color: '#1e40af', textDecoration: 'none' }}>
                  ✉️ Email broker
                </a>
              )}
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 32 }}>🔒 All documents are stored securely and encrypted</p>
      </div>
    </div>
  );
}
